import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { MultiSelect } from "@progress/kendo-react-dropdowns";
import { Popup } from "@progress/kendo-react-popup";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { button, icon, label, number, quote, specialCharacter } from "../../config";
import { onBlur, onFocus, onOpen } from "../../utils/kendo";

/**
 * Component for adding workspace owners with multi-select dropdown
 * @author Bhavana
 */
const AddWorkspaceOwner = (props) => {
  const { anchor, show, setShow, onSave, initialOwners = [] } = props;
  const [owners, setOwners] = useState(initialOwners);
  const [ownerError, setOwnerError] = useState(false);
  const [userData, setUsersData] = useState([]);

  const deleteRef = useRef(null);
  const blurTimeoutRef = useRef(null);

  const { assigneeList } = useSelector((state) => state?.tasks?.defaultDetails);

  /** Sets user data when assignee list changes
   * @author Bhavana */
  useEffect(() => {
    setUsersData(assigneeList);
  }, [assigneeList]);

  /** Updates owners when initial owners prop changes
   * @author Bhavana */
  useEffect(() => {
    setOwners(initialOwners);
  }, [initialOwners]);

  /** Filters assignee list and validates owners selection
   * @author Bhavana */
  useEffect(() => {
    const ownerIds = new Set(owners?.map((owner) => owner?.value));
    const filteredAssigneeList = assigneeList?.filter((assignee) => !ownerIds?.has(assignee?.value));
    setUsersData(filteredAssigneeList);
    owners?.length === number?.ZERO ? setOwnerError(true) : setOwnerError(false);
  }, [owners, assigneeList]);

  /** Filters the select options to be searched using different elements
   * @author Bhavana */
  const filterOptions = (event) => {
    const userData = assigneeList;
    if (!event?.filter?.value) {
      setUsersData(userData);
      return;
    }
    const filteredData = userData?.filter((user) => user?.label?.toLowerCase()?.includes(event?.filter?.value?.toLowerCase()) || user?.Email?.includes(event?.filter?.value));
    filteredData?.length > number?.ZERO ? setUsersData(filteredData) : setUsersData([]);
  };

  /**
   * Used to handle owner change
   * @author Bhavana
   * @param {Object} event
   */
  const ownerChangeHandler = (event) => {
    setOwners([...event.value]);
  };

  /**
   * handles saving owners
   * @param {*} event
   */
  const handleSaveOwners = (e) => {
    e.preventDefault();
    if (owners.length === 0) {
      setOwnerError(true);
      return;
    }
    if (onSave) {
      onSave(owners);
    }
    setShow(false);
  };

  const hideOnBlur = () => {
    setShow(false);
  };

  return (
    <React.Fragment>
      <Popup anchor={anchor.current} show={show} animate={true} onOpen={() => onOpen(deleteRef)} popupClass={"dt-popup add-workspace-owner-popup"}>
        <div className='create-project-popup' ref={deleteRef} tabIndex={number.ZERO} onFocus={() => onFocus(blurTimeoutRef)} onBlur={() => onBlur(blurTimeoutRef, hideOnBlur)}>
          <div className='dt-popup-header d-flex justify-content-between align-items-center pt-2 pb-2 pl-3 pr-3'>
            <div className='dt-popup-title'>{label.ADD_WORKSPACE_OWNER}</div>
            <span className='dt-popup-cancel-btn' title={label.CLOSE_POPUP} onClick={hideOnBlur}>
              <Tooltip anchorElement='target' parentTitle={true} position='bottom'>
                {icon.CLOSE}
              </Tooltip>
            </span>
          </div>
          <div className='dt-popup-body'>
            <div className='row'>
              <div className='form-group col-md-12'>
                <label htmlFor='workspace-owners'>
                  {label.QUEUE_OWNERS}
                  <span className='text-danger'>{specialCharacter.STAR}</span>
                </label>
                <MultiSelect
                  popupSettings={{
                    popupClass: "workspace-owners-dropdown",
                  }}
                  id='workspace-owners'
                  name='workspace-owners'
                  data={userData}
                  value={owners}
                  onChange={ownerChangeHandler}
                  textField='label'
                  dataItemKey='value'
                  filterable={true}
                  onFilterChange={filterOptions}
                />
                {ownerError && <small className='text-danger'>{quote.REQUIRED_OWNERS}</small>}
              </div>
            </div>
          </div>
          <div className='dt-popup-footer text-right'>
            <button className='btn btn-ghost mr-2' onClick={hideOnBlur}>
              {button.CANCEL}
            </button>
            <button type='submit' className='btn btn-primary' disabled={owners.length === number.ZERO} onClick={handleSaveOwners}>
              {button.SAVE}
            </button>
          </div>
        </div>
      </Popup>
    </React.Fragment>
  );
};

export default AddWorkspaceOwner;
